import 'nprogress/nprogress.css'; // Import base styles for the NProgress bar, necessary for proper styling when using "@vueuse/integrations/useNProgress"
import './assets/scss/hprofits.scss';

import * as Sentry from '@sentry/vue';
import { createBootstrap } from 'bootstrap-vue-next';
import { createApp } from 'vue';

import registerFilters from '@/utils/filters';
import UserSettings from '@/utils/userSettings.js';

import config from '../config/index';
import createApi from './api/index.js';
import App from './App.vue';
import { bootstrapAuthSession } from './auth/session';
import { legacyBootstrapOptions } from './bootstrapOptions';
import i18n, { ensureLocaleMessages, fallBackToDefaultLocale } from './i18n';
import { showRefreshFailedToast } from './plugins/errorToasts';
import registerPlugins from './plugins/index';
import vueQueryPlugin from './plugins/vueQuery';
import router from './router/index.js';
import store from './store/index.js';
import { buildAuthApiBase, normalizeAdnetApiHost } from './utils/adnetHost';
import registerComponents from './utils/components.js';
import registerDirectives from './utils/directives.js';
import registerErrorMessages from './utils/form.js';
import registerTestMarkup from './utils/testMarkup';

const app = createApp(App);
app.use(createBootstrap(legacyBootstrapOptions));

const adnetApiHost = normalizeAdnetApiHost(process.env.ADNET_API_HOST);
const authApiBase = buildAuthApiBase(adnetApiHost);

const authSession = bootstrapAuthSession(authApiBase);
authSession.onRefreshFailed(showRefreshFailedToast);

// Equivalent to Vue.prototype.$config, accessible via this.$config
app.config.globalProperties.$config = config;

if (process.env.SENTRY_URL) {
  Sentry.init({
    app,
    dsn: process.env.SENTRY_URL,
    release: `${process.env.PLATFORM}@${process.env.APP_VER}`,
    attachProps: true,
    tracesSampleRate: parseFloat(process.env.SENTRY_TRACES_SAMPLE_RATE || '0'),
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      // runDetached (and any app code) logs rejections via console.error; forward those to Sentry
      // here so the @hprofits/utils helper needs no Sentry coupling or shared mutable sink.
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
    environment: process.env.SENTRY_ENVIRONMENT,
    debug: process.env.SENTRY_DEBUG === 'true',
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

registerPlugins(app);
vueQueryPlugin(app);
registerFilters(app);
registerComponents(app);
registerDirectives(app);
registerErrorMessages(app);

// `data-component` markup for external Playwright E2E; STAGING is a build-time literal, so this
// call (and the side-effect-free module) is dead-code-eliminated from the prod bundle.
if (process.env.STAGING !== 'prod') {
  registerTestMarkup(app);
}

// Create api instance and inject into store
const api = createApi(
  [
    {
      name: 'auth',
      baseUrl: authApiBase,
      authRequired: true,
      lang: true,
    },
    {
      name: 'adnet',
      baseUrl: `${adnetApiHost}`,
      authRequired: true,
      lang: true,
      role: true,
    },
    {
      name: 'core',
      baseUrl: `${process.env.CORE_API_HOST}`,
    },
  ],
  {
    app,
  },
);

// Inject api into store BEFORE registering store with app
// This enables `this.$api` access in Vuex actions via store context
store.$api = api;

// Create global UserSettings instance with api reference
app.config.globalProperties.userSettings = new UserSettings(api);

app.use(store);

app.use(i18n);
app.use(router);
// TODO: do we nee to export store here or not in Vue3?
export { store };

// Version info on Shift+V
document.addEventListener('keydown', (e: KeyboardEvent) => {
  if (e.shiftKey && e.code === 'KeyV') {
    console.info({
      APP_VER: process.env.APP_VER,
      SENTRY_URL: process.env.SENTRY_URL,
      SENTRY_ENVIRONMENT: process.env.SENTRY_ENVIRONMENT,
      SENTRY_TRACES_SAMPLE_RATE: parseFloat(process.env.SENTRY_TRACES_SAMPLE_RATE || '0'),
      SENTRY_DEBUG: process.env.SENTRY_DEBUG,
      PLATFORM: process.env.PLATFORM,
      STAGING: process.env.STAGING,
    });
  }
});

/**
 * A stored non-English locale is only a locale until its catalogue chunk arrives, so mounting first
 * would paint the whole app in the English fallback and re-render it a moment later. The entry
 * module is the top of this chain — nothing above it can own the promise, so it is exported rather
 * than dropped.
 */
const bootstrap = async (): Promise<void> => {
  try {
    await ensureLocaleMessages(i18n.global.locale.value);
  } catch (error) {
    // A catalogue chunk that fails to load costs the stored locale, not the app: English ships in
    // the entry chunk, so mounting anyway renders it instead of a white screen. The locale itself
    // has to move with the messages — see `fallBackToDefaultLocale`.
    console.error('Locale catalogue failed to load; mounting with the fallback locale.', error);
    fallBackToDefaultLocale();
  }
  app.mount('#app');
};

export const mounted = bootstrap();
